<template>
  <div class="mt-2">
    <LoadingSpinner :loading="sessionLoading" />

    <div class="card shadow-sm mb-3">
      <div class="card-body">
        <div class="d-flex flex-wrap gap-3 align-items-end">
          <button
            class="btn btn-outline-primary btn-sm"
            @click="refreshSessions"
            :disabled="loading"
          >
            <i class="fas fa-sync-alt me-1" :class="{ 'fa-spin': loading }"></i>
            Refresh
          </button>
          <input
            type="text"
            class="form-control form-control-sm"
            :value="searchTerm"
            @input="$emit('searchTermChanged', $event.target.value)"
            placeholder="Search by session ID or title..."
            :disabled="loading"
            style="width: 220px;"
          />
          <select
            class="form-select form-select-sm"
            :value="filters.customerId"
            @change="$emit('filtersChanged', { ...filters, customerId: $event.target.value })"
            :disabled="loading"
            style="width: auto; min-width: 140px;"
          >
            <option value="">All Customers</option>
            <option v-for="customerId in uniqueCustomerIds" :key="customerId" :value="customerId">
              {{ getCustomerName(customerId) || customerId }}
            </option>
          </select>
          <select
            class="form-select form-select-sm"
            :value="filters.userId"
            @change="$emit('filtersChanged', { ...filters, userId: $event.target.value })"
            :disabled="loading"
            style="width: auto; min-width: 160px;"
          >
            <option value="">All Users</option>
            <option value="non-admin">Non-admin Users</option>
            <option v-for="userId in uniqueUserIds" :key="userId" :value="userId">
              {{ getUserName(userId) || userId }}
            </option>
          </select>
          <select
            class="form-select form-select-sm"
            :value="filters.initiationType"
            @change="$emit('filtersChanged', { ...filters, initiationType: $event.target.value })"
            :disabled="loading"
            style="width: auto; min-width: 140px;"
          >
            <option value="">All Types</option>
            <option v-for="type in uniqueInitiationTypes" :key="type" :value="type">
              {{ formatInitiationType(type) }}
            </option>
          </select>
          <select
            class="form-select form-select-sm"
            :value="filters.model"
            @change="$emit('filtersChanged', { ...filters, model: $event.target.value })"
            :disabled="loading"
            style="width: auto; min-width: 160px;"
          >
            <option value="">All Models</option>
            <option v-for="modelName in uniqueModels" :key="modelName" :value="modelName">
              {{ modelName }}
            </option>
          </select>
          <div class="d-flex align-items-center gap-1">
            <select
              class="form-select form-select-sm"
              :value="dateFilter"
              @change="$emit('dateFilterChanged', $event.target.value)"
              :disabled="loading"
              style="width: auto; min-width: 120px;"
            >
              <option value="all">All Time</option>
              <option value="yesterday">Yesterday</option>
              <option value="last3">Last 3 Days</option>
              <option value="last7">Last 7 Days</option>
              <option value="last30">Last 30 Days</option>
              <option value="custom">Custom</option>
            </select>
            <Datepicker
              v-if="dateFilter === 'custom'"
              :model-value="dates"
              :range="true"
              :clearable="false"
              :enableTimePicker="false"
              :disabled="loading"
              class="form-control-sm"
              style="width: 200px;"
              @update:model-value="onCustomDatesChanged"
            />
          </div>
          <div class="d-flex align-items-center gap-2 ms-auto">
            <button
              class="btn btn-outline-secondary btn-sm"
              @click="openExportModal"
              :disabled="loading"
            >
              <i class="fas fa-download me-1"></i>
              Export CSV
            </button>
            <div class="btn-group" role="group" aria-label="Smart Search version">
              <button
                type="button"
                class="btn btn-sm"
                :class="sessionsVersion === '1.0' ? 'btn-primary' : 'btn-outline-primary'"
                @click="$emit('sessionsVersionChanged', '1.0')"
              >
                1.0
              </button>
              <button
                type="button"
                class="btn btn-sm"
                :class="sessionsVersion === '2.0' ? 'btn-primary' : 'btn-outline-primary'"
                @click="$emit('sessionsVersionChanged', '2.0')"
              >
                2.0
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="sessions.length > 0" class="px-2 mb-2 text-muted small">
      <span v-if="hasActiveFilters">
        {{ filteredSessions.length }} of {{ sessions.length }} sessions
      </span>
      <span v-else>{{ sessions.length }} sessions</span>
    </div>

    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border text-primary mb-2" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <div class="text-primary">Loading sessions...</div>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <i class="fas fa-exclamation-triangle fa-2x text-warning mb-3"></i>
      <h5 class="text-muted mb-3">Failed to load sessions</h5>
      <p class="text-muted mb-3">{{ error }}</p>
      <button class="btn btn-outline-primary" @click="fetchSessions">
        <i class="fas fa-refresh me-2"></i>
        Retry
      </button>
    </div>

    <div v-else-if="sessions.length === 0" class="py-3">
      <p class="text-muted">No 2.0 sessions found.</p>
    </div>

    <div v-else-if="filteredSessions.length === 0" class="py-3">
      <p class="text-muted">No sessions match your search and filter criteria.</p>
    </div>

    <div v-else class="table-responsive table-container scrollable-container">
      <table class="table table-hover">
        <thead class="bg-gray-500 text-uppercase sticky-header">
          <tr>
            <th style="width: 130px;" class="sortable-header" @click="toggleSort('id')">
              ID <font-awesome-icon :icon="getSortIcon('id')" :class="getSortIconClass('id')" />
            </th>
            <th style="width: 300px;">Title</th>
            <th style="width: 120px;" class="sortable-header" @click="toggleSort('customer')">
              Customer <font-awesome-icon :icon="getSortIcon('customer')" :class="getSortIconClass('customer')" />
            </th>
            <th style="width: 100px;" class="sortable-header" @click="toggleSort('user')">
              User <font-awesome-icon :icon="getSortIcon('user')" :class="getSortIconClass('user')" />
            </th>
            <th style="width: 140px;">Initiation Type</th>
            <th style="width: 220px;">Context</th>
            <th style="width: 190px;">Model</th>
            <th style="width: 180px;" class="sortable-header" @click="toggleSort('created')">
              Created <font-awesome-icon :icon="getSortIcon('created')" :class="getSortIconClass('created')" />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="session in filteredSessions"
            :key="session.id"
            class="clickable-row"
            :class="{ 'loading-row': sessionLoading }"
            @click="viewSession(session)"
            :style="sessionLoading ? 'cursor: not-allowed;' : ''"
          >
            <td>
              <strong :title="session.id">{{ formatSessionId(session.id) }}</strong>
            </td>
            <td>
              <div class="text-truncate" style="max-width: 300px;" :title="session.title || 'Untitled'">
                {{ session.title || 'Untitled' }}
              </div>
            </td>
            <td>{{ getCustomerName(session.customerId) || '-' }}</td>
            <td>{{ getUserName(session.userId) || '-' }}</td>
            <td>{{ formatInitiationType(session.initiationType) || '-' }}</td>
            <td>
              <div class="text-truncate" style="max-width: 220px;" :title="getContextLabel(session.contextRef)">
                {{ getContextLabel(session.contextRef) }}
              </div>
            </td>
            <td>{{ session.model || '-' }}</td>
            <td>{{ formatTimestamp(session.createdAt) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <AdminSessionExportModal
      v-if="showExportModal"
      :prefill="exportPrefill"
      @closed="showExportModal = false"
    />
  </div>
</template>

<script>
import InsightsService from '../../services/InsightsService.js';
import AdminSessionExportModal from '../../components/AdminSessionExportModal.vue';
import Datepicker from 'vue3-date-time-picker';
import dayjs from 'dayjs';
import 'vue3-date-time-picker/dist/main.css';
import LoadingSpinner from '../../components/LoadingSpinner.vue';

export default {
  name: 'SessionsV2View',
  components: {
    Datepicker,
    LoadingSpinner,
    AdminSessionExportModal,
  },
  props: {
    viewModel: {
      type: Object,
      required: true,
    },
    sessionsVersion: {
      type: String,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
      default: () => ({
        customerId: '',
        userId: '',
        initiationType: '',
        model: '',
      }),
    },
    searchTerm: {
      type: String,
      required: true,
      default: '',
    },
    dateFilter: {
      type: String,
      required: true,
      default: 'all',
    },
    startDate: {
      type: Date,
      required: true,
      default: null,
    },
    endDate: {
      type: Date,
      required: true,
      default: null,
    },
    dates: {
      type: Array,
      required: true,
      default: () => [],
    },
    sorting: {
      type: Object,
      required: true,
      default: () => ({
        field: null,
        direction: null,
      }),
    },
  },
  data() {
    return {
      sessions: [],
      loading: false,
      sessionLoading: false,
      error: null,
      sessionsFetched: false,
      showExportModal: false,
      exportPrefill: {},
      insightsService: new InsightsService(),
    };
  },
  emits: [
    'sessionsVersionChanged',
    'sessionSelected',
    'filtersChanged',
    'searchTermChanged',
    'dateFilterChanged',
    'customDatesChanged',
    'sortingChanged',
  ],
  computed: {
    uniqueCustomerIds() {
      return [...new Set(this.sessions.map(session => session.customerId).filter(Boolean))].sort((a, b) => a - b);
    },
    uniqueUserIds() {
      let userIds = [...new Set(this.sessions.map(session => session.userId).filter(Boolean))];
      if (this.filters.customerId) {
        userIds = userIds.filter(userId =>
          this.sessions.some(session => session.userId === userId && session.customerId == this.filters.customerId)
        );
      }
      return userIds.sort((a, b) => a - b);
    },
    uniqueInitiationTypes() {
      return [...new Set(this.sessions.map(session => session.initiationType).filter(Boolean))].sort();
    },
    uniqueModels() {
      return [...new Set(this.sessions.map(session => session.model).filter(Boolean))].sort();
    },
    hasActiveFilters() {
      return Boolean(
        this.searchTerm.trim() ||
        this.filters.customerId ||
        this.filters.userId ||
        this.filters.initiationType ||
        this.filters.model ||
        this.dateFilter !== 'all'
      );
    },
    filteredSessions() {
      let filtered = this.sessions;

      if (this.searchTerm.trim()) {
        const search = this.searchTerm.toLowerCase().trim();
        filtered = filtered.filter(session =>
          String(session.id || '').toLowerCase().includes(search) ||
          String(session.title || '').toLowerCase().includes(search)
        );
      }
      if (this.filters.customerId) {
        filtered = filtered.filter(session => session.customerId == this.filters.customerId);
      }
      if (this.filters.userId === 'non-admin') {
        filtered = filtered.filter(
          session => !this.isAdminUser(session.userId) && Number(session.customerId) !== 1
        );
      } else if (this.filters.userId) {
        filtered = filtered.filter(session => session.userId == this.filters.userId);
      }
      if (this.filters.initiationType) {
        filtered = filtered.filter(session => session.initiationType === this.filters.initiationType);
      }
      if (this.filters.model) {
        filtered = filtered.filter(session => session.model === this.filters.model);
      }
      if (this.startDate && this.endDate) {
        filtered = filtered.filter(session => {
          const createdAt = new Date(session.createdAt);
          return createdAt >= this.startDate && createdAt <= this.endDate;
        });
      }
      if (this.sorting.field && this.sorting.direction) {
        filtered = [...filtered].sort((a, b) => {
          const aValue = this.getSortValue(a, this.sorting.field);
          const bValue = this.getSortValue(b, this.sorting.field);
          const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
          return this.sorting.direction === 'asc' ? comparison : -comparison;
        });
      }

      return filtered;
    },
  },
  watch: {
    'filters.customerId'(newCustomerId, oldCustomerId) {
      if (oldCustomerId && newCustomerId !== oldCustomerId && this.filters.userId && this.filters.userId !== 'non-admin') {
        this.$emit('filtersChanged', { ...this.filters, userId: '' });
      }
    },
  },
  mounted() {
    this.fetchSessions();
  },
  methods: {
    async fetchSessions() {
      this.loading = true;
      this.error = null;

      try {
        const response = await this.insightsService.getAdminSessions();

        let data = [];
        if (response && response.data && response.data.data && Array.isArray(response.data.data)) {
          data = response.data.data;
        } else if (response && response.data && Array.isArray(response.data)) {
          data = response.data;
        } else if (response && Array.isArray(response)) {
          data = response;
        } else {
          data = [];
        }

        this.sessions = data;
        this.sessionsFetched = true;
      } catch (error) {
        console.error('Error fetching admin sessions:', error);
        this.sessions = [];
        this.error = error.message || 'Failed to load sessions';
      } finally {
        this.loading = false;
      }
    },
    async refreshSessions() {
      this.sessionsFetched = false;
      await this.fetchSessions();
    },
    openExportModal() {
      this.exportPrefill = {
        filters: { ...this.filters },
        dateFilter: this.dateFilter,
        startDate: this.startDate,
        endDate: this.endDate,
      };
      this.showExportModal = true;
    },
    async viewSession(session) {
      if (this.sessionLoading) {
        return;
      }

      this.sessionLoading = true;
      try {
        const details = await this.insightsService.getAdminSessionDetails(session.id);
        this.$emit('sessionSelected', { session, details });
      } catch (error) {
        console.error('Error fetching session details:', error);
      } finally {
        this.sessionLoading = false;
      }
    },
    onCustomDatesChanged(dates) {
      if (dates && dates.length === 2) {
        this.$emit('customDatesChanged', dates);
      }
    },
    toggleSort(field) {
      let newSorting = { ...this.sorting };

      if (newSorting.field === field) {
        if (newSorting.direction === 'asc') {
          newSorting.direction = 'desc';
        } else if (newSorting.direction === 'desc') {
          newSorting.field = null;
          newSorting.direction = null;
        }
      } else {
        newSorting.field = field;
        newSorting.direction = 'asc';
      }

      this.$emit('sortingChanged', newSorting);
    },
    getSortValue(session, field) {
      if (field === 'id') return session.id || '';
      if (field === 'customer') return this.getCustomerName(session.customerId) || '';
      if (field === 'user') return this.getUserName(session.userId) || '';
      if (field === 'created') return new Date(session.createdAt).getTime();
      return '';
    },
    getSortIcon(field) {
      if (this.sorting.field !== field) return 'sort';
      return this.sorting.direction === 'asc' ? 'sort-up' : 'sort-down';
    },
    getSortIconClass(field) {
      return this.sorting.field === field ? 'text-primary' : 'text-muted';
    },
    formatSessionId(id) {
      if (!id) return '-';
      return id.length > 12 ? `${id.slice(0, 8)}…` : id;
    },
    formatTimestamp(timestamp) {
      return timestamp ? dayjs(timestamp).format('MMM D, YYYY h:mm A') : '-';
    },
    formatInitiationType(type) {
      if (!type) return '';
      return type
        .replace(/_/g, ' ')
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    getContextLabel(contextRef) {
      if (!contextRef) return '-';
      if (contextRef.label) return contextRef.label;
      if (!contextRef.kind) return '-';
      return contextRef.kind
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, character => character.toUpperCase());
    },
    getUserName(userId) {
      if (!userId || !streetmetrics.model.users) return null;
      const user = streetmetrics.model.users.find(candidate => candidate.userId === userId);
      return user ? user.displayName : null;
    },
    isAdminUser(userId) {
      return this.getUserName(userId) === 'Admin';
    },
    getCustomerName(customerId) {
      if (!customerId || !streetmetrics.model.customers) return null;
      const customer = streetmetrics.model.customers.find(candidate => candidate.customerId === customerId);
      return customer ? customer.customerName : null;
    },
  },
};
</script>

<style scoped>
.table-container {
  max-height: 70vh;
}

.scrollable-container {
  overflow-y: auto;
}

.sortable-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

.sortable-header:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
}

.sticky-header th {
  position: sticky;
  top: 0;
  background-color: inherit;
  z-index: 10;
}

.clickable-row {
  cursor: pointer;
}

.clickable-row:hover {
  background-color: rgba(0, 123, 255, 0.05);
}

.loading-row {
  opacity: 0.6;
}
</style>
